import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { journalsQueries } from "@/entities/journal";
import { useValidateTitle } from "@/features/journal-title-validation";
import { useCoordinator, useNavigationPosition } from "@/shared/lib/hooks";
import { useGetRepresentative } from "../../../../api";
import { titleSectionsConfig } from "../../../journals-title/model/title-sections-config";
import "./side-journals-list-new.scss";

const BlueDotIcon = () => (
  <svg
    width="6"
    height="6"
    viewBox="0 0 6 6"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="3" cy="3" r="3" fill="rgba(65, 105, 225, 1)" />
  </svg>
);

const YellowDotIcon = () => (
  <svg
    width="6"
    height="6"
    viewBox="0 0 6 6"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="3" cy="3" r="3" fill="rgba(242, 175, 45, 1)" />
  </svg>
);

const GrayDotIcon = () => (
  <svg
    width="6"
    height="6"
    viewBox="0 0 6 6"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="3" cy="3" r="3" fill="rgba(119, 119, 119, 1)" />
  </svg>
);

export const SideJournalsListNew = () => {
  const { errorList } = useValidateTitle();
  const { titleId } = useCoordinator();
  const { isTitleAmendmentsForm } = useNavigationPosition();
  const { data } = useQuery(journalsQueries.getTitleJournal({ titleId }));

  const { representative: developer } = useGetRepresentative("developer");
  const { representative: techCustomer } =
    useGetRepresentative("technicalCustomer");
  const { representative: buildingContractor } =
    useGetRepresentative("buildingContractor");
  const { representative: designerSupervisions } = useGetRepresentative(
    "designerSupervisions",
  );
  const { representative: otherDevelopers } =
    useGetRepresentative("otherDevelopers");
  const { representative: projectDocumentationContractor } =
    useGetRepresentative("projectDocumentationContractor");

  const permissions =
    data?.mainJournal?.titleJournal?.documentation?.documentationPermission;
  const expertise =
    data?.mainJournal?.titleJournal?.documentation?.documentationExamination;
  const activePermission = permissions?.find(
    (p) => p.selectedDocumentPermission,
  );
  const activeExamination = expertise?.find(
    (p) => p.selectedDocumentExamination,
  );

  const isSectionEmpty = useMemo(() => {
    return (sectionId: string): boolean => {
      switch (sectionId) {
        case "developer-company":
          return !developer?.company;
        case "tec-customer-company":
          return !techCustomer?.company;
        case "building-contractor-company":
          return !buildingContractor?.company;
        case "designer-supervisions":
          return !designerSupervisions || designerSupervisions.length === 0;
        case "other-developers":
          return !otherDevelopers || otherDevelopers.length === 0;
        case "documentation-contractor-company":
          return !projectDocumentationContractor?.company;
        case "documentation-permission":
          return !activePermission;
        case "documentation-examination":
          return !activeExamination;
        default:
          return false;
      }
    };
  }, [
    developer,
    techCustomer,
    buildingContractor,
    designerSupervisions,
    otherDevelopers,
    projectDocumentationContractor,
    activePermission,
    activeExamination,
  ]);

  const errorsBySectionId = useMemo(() => {
    const countErrors = (ids: number[]) =>
      errorList.filter((error) => ids.includes(error.id)).length;
    const result: Record<string, number> = {};

    result["journals-info"] = countErrors([1, 2, 3, 4]);

    result["developer-company"] = developer?.company ? countErrors([6, 15]) : 0;
    result["tec-customer-company"] = techCustomer?.company
      ? countErrors([7, 8, 15, 26, 27])
      : 0;
    result["building-contractor-company"] = buildingContractor?.company
      ? countErrors([10, 25])
      : 0;

    result["designer-supervisions"] =
      designerSupervisions && designerSupervisions.length > 0
        ? countErrors([18])
        : 0;
    result["other-developers"] =
      otherDevelopers && otherDevelopers.length > 0 ? countErrors([21, 32]) : 0;

    result["documentation-contractor-company"] = countErrors([33, 30]);
    result["documentation-permission"] = countErrors([999]);
    result["documentation-examination"] = countErrors([998]);

    return result;
  }, [
    errorList,
    developer,
    techCustomer,
    buildingContractor,
    designerSupervisions,
    otherDevelopers,
  ]);

  const getDotIcon = (sectionId: string) => {
    const errorCount = errorsBySectionId[sectionId] || 0;
    const isEmpty = isSectionEmpty(sectionId);

    if (isEmpty) {
      return (
        <div className="side-journals-list__icon">
          <GrayDotIcon />
        </div>
      );
    }

    if (errorCount === 0) {
      return (
        <div className="side-journals-list__icon">
          <BlueDotIcon />
        </div>
      );
    }
    return (
      <div className="side-journals-list__icon">
        <YellowDotIcon />
      </div>
    );
  };

  const moveToId = (id: string) => {
    if (id) {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  return (
    <div className="side-journals-list">
      <div className="side-journals-list__items">
        {isTitleAmendmentsForm ? (
          <div
            className="side-journals-list__item"
            onClick={() => moveToId("title-change-reason")}
          >
            <div className="side-journals-list__icon">
              <BlueDotIcon />
            </div>
            <div className="side-journals-list__item-title">
              Причина и основание изменений
            </div>
          </div>
        ) : null}
        {titleSectionsConfig.map((section) => {
          return (
            <div
              key={section.id}
              className="side-journals-list__item"
              onClick={() => moveToId(section.anchorId)}
            >
              {getDotIcon(section.id)}
              <div className="side-journals-list__item-title">
                {section.title}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
